import {DataTable} from "../DataTable/DataTable";
import {firstOrUndefined, SiteEntity} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {DefaultText} from "../../text/DefaultText/DefaultText";
import {SiteTypeBadge} from "../../badge/SiteTypeBadge/SiteTypeBadge";
import {sortBy} from "lodash";
import {useSiteDataTableFilter} from "./useSiteDataTableFilter";
import {usePagination} from "../DataTable/usePagination";
import {getColumnFilterProps} from "../DataTable/useDataTableFilter";
import {useDataTableSorter} from "../DataTable/useDataTableSorter";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";
import {EntityAnchor} from "../../anchor/EntityAnchor/EntityAnchor";
import {useMemo} from "react";

const EMPTY_ARRAY: SiteEntity[] = []

export type SiteDataTableProps = EntityDataTableProps<SiteEntity>

export const SiteDataTable = (props: SiteDataTableProps) => {
  const {
    records: sites = EMPTY_ARRAY,
    recordsPerPageOptions,
    ...rest
  } = props

  const {t} = useTranslation(["common"]);

  const {filteredRecords, filterConfig} = useSiteDataTableFilter({
    records: sites
  })

  const sortConfig = useMemo(() => {
    return {
      "name": {
        sortAll: (records, columnAccessor) => {
          return sortBy(records, [function (record) {
            return record.name?.toLowerCase()
          }])
        }
      },
    }
  }, [])

  const {
    sortedRecords,
    sortStatus,
    onSortStatusChange,
  } = useDataTableSorter<SiteEntity>({
    records: filteredRecords,
    sortStatus: {columnAccessor: 'name', direction: 'asc'},
    sortConfig: sortConfig,
  })

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  return (
    <DataTable
      {...rest}
      {...(pagination ?? {})}
      sortStatus={sortStatus}
      onSortStatusChange={onSortStatusChange}
      columns={[
        {
          ...getColumnFilterProps(filterConfig, "name"),
          title: t("table.entity.site.column.name", "Name"),
          accessor: "name",
          width: 100,
          sortable: true,
          render: (record) => (
            <EntityAnchor value={record}/>
          ),
        },
        {
          ...getColumnFilterProps(filterConfig, "route"),
          title: t("table.entity.site.column.route", "Route"),
          accessor: "routes",
          width: 100,
          sortable: true,
          render: ({routes}) => (firstOrUndefined(routes)?.name)
        },
        {
          ...getColumnFilterProps(filterConfig, "operatingArea"),
          title: t("table.entity.site.column.operatingarea", "Operating Area"),
          accessor: "operatingAreaName",
          width: 100,
          sortable: true,
          render: ({operatingAreaId, operatingAreaName}) => (
            operatingAreaId ? <EntityAnchor value={{
              objectId: operatingAreaId,
              objectName: operatingAreaName,
              objectType: "OPERATINGAREA",
            }}/> : undefined
          )
        },
        {
          ...getColumnFilterProps(filterConfig, "type"),
          title: t("table.entity.site.column.type", "Type"),
          accessor: "type",
          width: 100,
          sortable: true,
          render: ({type}) => (
            type ? <SiteTypeBadge type={type}/> : undefined
          )
        },
        {
          ...getColumnFilterProps(filterConfig, "company"),
          title: t("table.entity.site.column.operator", "Operator"),
          accessor: "companyName",
          sortable: true,
          width: 100,
          render: ({companyId, companyName}) => (
            companyId ? <DefaultText value={companyName}/> : undefined
          )
        }
      ]}
    />
  );
};
